import { useMemo } from "react";
import { useEditorStore } from "./editorStore";

export function useTheoremNumbers() {
  const blocks = useEditorStore((s) => s.blocks);
  return useMemo(
    () =>
      blocks.reduce(
        (acc, block) => {
          if (block.type === "theorem") {
            const theoremType = (block.content?.theoremType as string) || "theorem";
            if (theoremType !== "proof") {
              acc.counts[theoremType] = (acc.counts[theoremType] || 0) + 1;
              acc.numbers[block.id] = acc.counts[theoremType];
            }
          }
          return acc;
        },
        { counts: {} as Record<string, number>, numbers: {} as Record<string, number> }
      ),
    [blocks]
  );
}

export function useFootnoteNumbers() {
  const blocks = useEditorStore((s) => s.blocks);
  return useMemo(
    () =>
      blocks.reduce(
        (acc, block) => {
          if (block.type === "footnote") {
            acc.count += 1;
            acc.numbers[block.id] = acc.count;
          }
          return acc;
        },
        { count: 0, numbers: {} as Record<string, number> }
      ),
    [blocks]
  );
}

export function useHeadings() {
  const blocks = useEditorStore((s) => s.blocks);
  return useMemo(
    () =>
      blocks
        .filter((b) => b.type === "heading")
        .map((b) => ({
          id: b.id,
          text: (b.content?.text as string) || "",
          level: (b.content?.level as number) || 1,
        })),
    [blocks]
  );
}

export function useBlockById(id: string) {
  return useEditorStore((s) => s.blocks.find((b) => b.id === id));
}

export function useBlocks() {
  return useEditorStore((s) => s.blocks);
}

export function useTitle() {
  return useEditorStore((s) => s.title);
}

export function useDocumentId() {
  return useEditorStore((s) => s.documentId);
}

export function useDocumentLatexMeta() {
  const document = useEditorStore((s) => s.document);
  return useMemo(() => ({
    documentClass: document?.latexDocumentClass,
    documentClassOptions: document?.latexDocumentClassOptions,
    latexPackages: document?.latexPackages,
  }), [document]);
}

export function useBibliographyEntries() {
  const document = useEditorStore((s) => s.document);
  return useMemo(() => {
    if (!document?.bibliography) return [];
    return document.bibliography.map((b) => ({
      id: b.id,
      citeKey: b.citeKey,
      entryType: b.entryType,
      fields: (b.data || b.fields || {}) as Record<string, string>,
    }));
  }, [document]);
}
